import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSystemSetting,
  createSystemSetting,
  updateSystemSetting,
  type CreateSystemSettingRequest,
  type UpdateSystemSettingRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const CATEGORIES = ['general', 'email', 'security', 'appearance'];
const VALUE_TYPES = ['string', 'number', 'boolean', 'json'];

interface SettingFormValues {
  key: string;
  value: string;
  category: string;
  description: string;
  value_type: string;
  is_public: boolean;
}

export default function SystemSettingForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<SettingFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getSystemSetting(id)
      .then((setting) => {
        form.setFieldsValue({
          key: setting.key,
          value: setting.value,
          category: setting.category,
          description: setting.description,
          value_type: setting.value_type,
          is_public: setting.is_public,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: SettingFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateSystemSettingRequest = {
          value: values.value,
          category: values.category,
          description: values.description,
          value_type: values.value_type,
          is_public: values.is_public,
        };
        await updateSystemSetting(id, dto);
        message.success(t('admin.setting_update_success'));
      } else {
        const dto: CreateSystemSettingRequest = {
          key: values.key,
          value: values.value,
          category: values.category,
          description: values.description,
          value_type: values.value_type,
          is_public: values.is_public,
        };
        await createSystemSetting(dto);
        message.success(t('admin.setting_create_success'));
      }
      navigate('/admin/system-settings');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('admin', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/system-settings')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('admin.edit_system_setting_title') : t('admin.create_system_setting_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ category: 'general', value_type: 'string', is_public: false }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="key"
          label={t('admin.field.key')}
          rules={[{ required: true, message: t('admin.validation.key_required') }]}
        >
          <Input disabled={isEdit} placeholder={t('admin.field.key')} />
        </Form.Item>

        <Form.Item
          name="value"
          label={t('admin.field.value')}
          rules={[{ required: true, message: t('admin.validation.value_required') }]}
        >
          <Input.TextArea rows={3} placeholder={t('admin.field.value')} />
        </Form.Item>

        <Form.Item name="category" label={t('admin.field.category')}>
          <Select>
            {CATEGORIES.map((c) => (
              <Select.Option key={c} value={c}>
                {t(`admin.category_options.${c}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="value_type" label={t('admin.field.value_type')}>
          <Select>
            {VALUE_TYPES.map((vt) => (
              <Select.Option key={vt} value={vt}>
                {t(`admin.value_type_options.${vt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label={t('admin.field.description')}>
          <Input.TextArea rows={2} placeholder={t('admin.field.description')} />
        </Form.Item>

        <Form.Item name="is_public" label={t('admin.field.is_public')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/admin/system-settings')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
